import dayjs from 'dayjs';
// ** React Imports
import { lazy, Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import { AuthProvider } from 'react-oidc-context';
import { BrowserRouter } from 'react-router-dom';

// ** Ripple Button
import '@core/components/ripple-button';

import { oidcConfig } from './setup/config/oidc';

import SessionManager from '@components/session/SessionManager';
import UILoader from '@core/components/ui-loader';
import initializeApp from '@store/actions/initialize';
import { restoreCache } from '@store/apollo/persist';
import weekOfMonthPlugin from '@utils/dayjs/weekOfMonth.plugin';

// ** Core styles
import '@fonts/feather/iconfont.css';
import '@styles/core.scss';
import '@assets/scss/style.scss';
import 'react-perfect-scrollbar/dist/css/styles.css';
// ** React Toastify
import '@styles/react/libs/toastify/toastify.scss';

// ** Lazy load app
const LazyApp = lazy(() => import('./app/App'));

dayjs.extend(weekOfMonthPlugin);

const renderApp = async () => {
  const container = document.getElementById('root');
  const root = createRoot(container);

  // Hydrate the Apollo cache from localStorage BEFORE mounting React. If we
  // mount first, queries fire against an empty cache, then the restore lands
  // and triggers a second render — the user sees a flash of loading state for
  // data that was already on disk. The restore is fast (typically < 50ms) and
  // bounded by maxSize, so blocking on it is the right trade.
  await restoreCache();

  initializeApp();
  root.render(
    <AuthProvider {...oidcConfig}>
      <BrowserRouter basename={''}>
        <SessionManager>
          <Suspense fallback={(
            <UILoader
              className={'full-page hide-text'}
              blocking
            />
          )}
          >
            <LazyApp />
          </Suspense>
        </SessionManager>
      </BrowserRouter>
    </AuthProvider>
  );
};

renderApp().catch((err) => {
  // Boot-time failure (cache restore should never throw — restoreCache has its
  // own try/catch — but a failure here means we couldn't even reach React).
  // Surface for the user instead of a blank page.
  // eslint-disable-next-line no-console
  console.error('[boot] failed to start app:', err);
  const container = document.getElementById('root');
  if (container) {
    container.innerHTML = '<div style="padding:2rem;font-family:sans-serif">Application failed to start. Please reload.</div>';
  }
});
